import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import { IconBookmark, IconChevronLeft, IconClockHour4, IconHeart, IconLink, IconMessageCircle, IconPhoto, IconSend, IconPlus } from '@tabler/icons-react';
import { withUsername, formatDelay } from '../../utils/format';
import './styles.scss';

// Who the preview pretends to be talking to.
const SAMPLE = 'maria.fit';
const EMAIL = 'maria@email.com';

// The conversation an automation leads to, up to `until` (`messages`: before the delivery).
function threadOf(automation, { until, sampleKeyword, t, locale }) {
  const { trigger, dm } = automation;
  const say = text => withUsername(text, SAMPLE);
  const rows = [];
  const contact = (key, text) => { if (text) rows.push({ key, side: 'in', text }); };
  const bot = (key, text, extra = {}) => rows.push({ key, side: 'out', text: say(text) || null, empty: !text, ...extra });
  if (trigger.channel === 'stories') rows.push({ key: 'story', side: 'in', story: true, text: sampleKeyword });
  if (trigger.channel === 'direct_messages') contact('first', sampleKeyword);
  if (dm.opening.enabled) { bot('opening', dm.opening.text, { button: dm.opening.button_label }); contact('opening-tap', dm.opening.button_label); }
  if (dm.follow_gate.enabled) { bot('follow', dm.follow_gate.text, { button: dm.follow_gate.button_label }); contact('follow-tap', dm.follow_gate.button_label); }
  if (dm.email_capture.enabled) { bot('email', dm.email_capture.text); contact('email-reply', EMAIL); }
  if (until === 'messages') return rows;
  bot('delivery', dm.delivery.text, { links: dm.delivery.buttons });
  if (dm.reminder.enabled) {
    rows.push({ key: 'delay', side: 'time', text: t('previewReminderAfter', { delay: formatDelay(dm.reminder.delay_minutes, locale) }) });
    bot('reminder', dm.reminder.message.text, { links: dm.reminder.message.buttons });
  }
  return rows;
}

function Avatar({ account, small }) {
  const picture = account?.picture?.url;
  return <span className={`studio-phone__avatar${small ? ' is-small' : ''}`} aria-hidden="true">
    {picture ? <img src={picture} alt="" /> : <Text.caption>{(account?.handle || 'g').charAt(0).toUpperCase()}</Text.caption>}
  </span>;
}

// A generic phone showing what the person goes through. It stays dark in both themes, so every
// element keeps its own ink (the global colour rule would otherwise reach it).
export default function PhonePreview({ automation, scene, account, media, locale, until }) {
  const { t } = useTranslation('studioAutomations');
  const keyword = automation.trigger.keywords[0] || t('previewAnyWord');
  const reply = automation.comment_reply.enabled ? automation.comment_reply.templates.find(item => item.trim()) : null;
  const rows = useMemo(() => (scene === 'chat' ? threadOf(automation, { until, sampleKeyword: keyword, t, locale }) : []), [automation, scene, until, keyword, t, locale]);
  const thread = useRef(null);
  useEffect(() => { if (thread.current) thread.current.scrollTop = thread.current.scrollHeight; }, [rows]);
  const handle = account?.handle || 'seu.perfil';
  const photo = media?.preview?.url;
  const picture = photo ? <img className="studio-phone__photo" src={photo} alt="" /> : <span className="studio-phone__photo is-empty"><IconPhoto /></span>;

  const post = <>
    <div className="studio-phone__bar"><IconChevronLeft /><span className="studio-phone__bar-title"><small>{handle}</small><strong>{t('previewPost')}</strong></span></div>
    <div className="studio-phone__author"><Avatar account={account} small /><strong>{handle}</strong></div>
    {picture}
    <div className="studio-phone__actions"><IconHeart /><IconMessageCircle /><IconSend /><span /><IconBookmark /></div>
    {media?.caption && <p className="studio-phone__caption"><strong>{handle}</strong> {media.caption}</p>}
  </>;

  return <div className={`studio-phone is-${scene}`} role="img" aria-label={t('previewLabel')}>
    <div className="studio-phone__screen">
      <div className="studio-phone__status" aria-hidden="true"><span>9:41</span><i /></div>
      {scene === 'post' && post}
      {scene === 'comments' && <>
        <div className="studio-phone__dimmed">{post}</div>
        <div className="studio-phone__sheet">
          <span className="studio-phone__grip" />
          <strong className="studio-phone__sheet-title">{t('previewComments')}</strong>
          <div className="studio-phone__comment"><span className="studio-phone__avatar is-small is-person"><Text.caption>M</Text.caption></span><p><strong>{SAMPLE}</strong> <small>{t('previewNow')}</small><br />{keyword}</p></div>
          {reply && <div className="studio-phone__comment is-reply"><Avatar account={account} small /><p><strong>{handle}</strong> <small>{t('previewNow')}</small><br />{withUsername(reply, SAMPLE)}</p></div>}
        </div>
      </>}
      {scene === 'story' && <>
        <div className="studio-phone__story">
          {photo ? <img src={photo} alt="" /> : <span className="is-empty"><IconPhoto /></span>}
          <div className="studio-phone__story-author"><Avatar account={account} small /><strong>{handle}</strong></div>
        </div>
        <div className="studio-phone__story-reply"><span>{keyword}</span><IconSend /></div>
      </>}
      {scene === 'chat' && <>
        <div className="studio-phone__bar is-chat"><IconChevronLeft /><Avatar account={account} small /><strong>{handle}</strong></div>
        <div ref={thread} className="studio-phone__thread">
          {rows.map(row => {
            if (row.side === 'time') return <span key={row.key} className="studio-phone__time"><IconClockHour4 />{row.text}</span>;
            if (row.story) return <div key={row.key} className="studio-phone__bubble is-in is-story">{photo && <img src={photo} alt="" />}<span>{row.text}</span></div>;
            return <div key={row.key} className={`studio-phone__bubble is-${row.side}${row.empty ? ' is-blank' : ''}`}>
              <span>{row.text || t('previewEmptyMessage')}</span>
              {row.button && <span className="studio-phone__button">{row.button}</span>}
              {row.links?.map((link, index) => <span key={index} className="studio-phone__button"><IconLink />{link.title || t('previewLink')}</span>)}
            </div>;
          })}
        </div>
        <div className="studio-phone__composer" aria-hidden="true"><span>{t('previewMessage')}</span><IconPlus /></div>
      </>}
    </div>
  </div>;
}
